import { z } from "zod/v4";
/**
 * Unified paint catalog admin contracts.
 *
 * All mutations carry a `kind` discriminator so a single repository
 * function can handle both tile and overlay branches. The same set of
 * mutations powers `/admin/system-catalog` (kind=tile) and `/admin/overlays`
 * (kind=overlay).
 */
export const paintKindSchema = z.enum(["tile", "overlay"]);
export const paintSourceSchema = z.enum(["system", "community"]);
export const paintAvailabilitySchema = z.enum(["available", "unavailable", "inherit"]);
export const paintAssetAvailabilitySchema = z.enum(["available", "unavailable"]);
export const paintModeSchema = z.enum(["ordered", "dynamic"]);
// ---------------------------------------------------------------------------
// Folder (taxonomy_node) mutations
// ---------------------------------------------------------------------------
export const upsertPaintTaxonomyNodeInputSchema = z.object({
    id: z.string().optional(),
    kind: paintKindSchema,
    parentId: z.string().nullable().optional(),
    label: z.string().min(1),
    sortOrder: z.number().optional(),
    source: paintSourceSchema.optional(),
    availability: paintAvailabilitySchema.optional(),
});
export const movePaintTaxonomyNodeInputSchema = z.object({
    id: z.string(),
    kind: paintKindSchema,
    targetParentId: z.string().nullable(),
});
export const reorderPaintTaxonomyNodesInputSchema = z.object({
    kind: paintKindSchema,
    parentId: z.string().nullable(),
    ids: z.array(z.string()),
});
export const reorderPaintFolderContentsInputSchema = z.object({
    kind: paintKindSchema,
    taxonomyNodeId: z.string().nullable(),
    items: z.array(z.discriminatedUnion("kind", [
        z.object({ kind: z.literal("folder"), id: z.string() }),
        z.object({ kind: z.literal("paint-set"), id: z.string() }),
    ])),
});
export const deletePaintTaxonomyNodeInputSchema = z.object({
    id: z.string(),
    kind: paintKindSchema,
});
export const setPaintTaxonomyNodeAvailabilityInputSchema = z.object({
    id: z.string(),
    kind: paintKindSchema,
    availability: paintAvailabilitySchema,
});
// ---------------------------------------------------------------------------
// Paint set mutations
// ---------------------------------------------------------------------------
export const upsertPaintSetInputSchema = z.object({
    id: z.string().optional(),
    kind: paintKindSchema,
    title: z.string().min(1),
    paintMode: paintModeSchema.optional(),
    source: paintSourceSchema.optional(),
    availability: paintAvailabilitySchema.optional(),
    taxonomyNodeIds: z.array(z.string().nullable()).optional(),
});
export const setPaintSetPaintModeInputSchema = z.object({
    id: z.string(),
    kind: paintKindSchema,
    paintMode: paintModeSchema,
});
export const setPaintSetAvailabilityInputSchema = z.object({
    id: z.string(),
    kind: paintKindSchema,
    availability: paintAvailabilitySchema,
});
export const deletePaintSetInputSchema = z.object({
    id: z.string(),
    kind: paintKindSchema,
});
export const reorderPaintSetsInFolderInputSchema = z.object({
    kind: paintKindSchema,
    taxonomyNodeId: z.string(),
    ids: z.array(z.string()),
});
export const attachPaintSetToFolderInputSchema = z.object({
    kind: paintKindSchema,
    paintSetId: z.string(),
    taxonomyNodeId: z.string(),
});
export const detachPaintSetFromFolderInputSchema = z.object({
    kind: paintKindSchema,
    paintSetId: z.string(),
    taxonomyNodeId: z.string(),
});
// ---------------------------------------------------------------------------
// Paint asset mutations
// ---------------------------------------------------------------------------
export const upsertPaintAssetInputSchema = z.object({
    id: z.string().optional(),
    kind: paintKindSchema,
    assetId: z.string(),
    namespace: z.string(),
    ownerKey: z.string().nullable().optional(),
    title: z.string().min(1),
    description: z.string().optional(),
    source: paintSourceSchema.optional(),
    isCustom: z.boolean().optional(),
    sortOrder: z.number().optional(),
    paintSetId: z.string().optional(),
});
export const deletePaintAssetInputSchema = z.object({
    id: z.string(),
    kind: paintKindSchema,
});
export const setPaintAssetAvailabilityInputSchema = z.object({
    id: z.string(),
    kind: paintKindSchema,
    availability: paintAssetAvailabilitySchema,
});
export const movePaintAssetBetweenSetsInputSchema = z.object({
    kind: paintKindSchema,
    paintAssetId: z.string(),
    sourcePaintSetId: z.string(),
    destinationPaintSetId: z.string(),
});
export const attachPaintAssetToPaintSetInputSchema = z.object({
    kind: paintKindSchema,
    paintAssetId: z.string(),
    paintSetId: z.string(),
});
export const reorderPaintSetEntriesInputSchema = z.object({
    kind: paintKindSchema,
    paintSetId: z.string(),
    paintAssetIds: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// Nested paint_set entry mutations (paint_set inside another paint_set)
// ---------------------------------------------------------------------------
export const attachChildPaintSetToPaintSetInputSchema = z.object({
    kind: paintKindSchema,
    parentPaintSetId: z.string(),
    childPaintSetId: z.string(),
    sortOrder: z.number().int().min(0).optional(),
});
export const detachPaintSetEntryInputSchema = z.object({
    entryId: z.string(),
});
// ---------------------------------------------------------------------------
// Multi-set bulk operations (admin multi-select)
// ---------------------------------------------------------------------------
export const mergePaintSetsInputSchema = z.object({
    kind: paintKindSchema,
    targetPaintSetId: z.string(),
    sourcePaintSetIds: z.array(z.string()).min(1),
    paintMode: paintModeSchema.optional(),
});
export const splitPaintSetInputSchema = z.object({
    kind: paintKindSchema,
    paintSetId: z.string(),
});
export const bulkMovePaintSetsInputSchema = z.object({
    kind: paintKindSchema,
    paintSetIds: z.array(z.string()).min(1),
    targetTaxonomyNodeId: z.string().nullable(),
});
export const bulkSetPaintSetAvailabilityInputSchema = z.object({
    kind: paintKindSchema,
    paintSetIds: z.array(z.string()).min(1),
    availability: paintAvailabilitySchema,
});
